import { api } from './api';
import { FASTIFY_BASE_URL } from '../config/apiConfig';

/**
 * Server fallback for PostHog feature flags.
 *
 * When the browser can't reach PostHog (tracker blockers, browser tracking
 * protection, corporate web filters blocking p.lodgeic.io), posthog-js still
 * marks flags as loaded — with every flag false. Flag-gated pages (Journal,
 * Lodgeic AI, Budgeting & Forecasting, …) then disappear from the sidebar and
 * their routes bounce to the dashboard, even though the user has access.
 *
 * `trackFlagLoads` (posthog.init `loaded` hook, so it sees the very first
 * request) notes each failed load; MainLayout's usePosthogFlagFallback then
 * asks our API, which evaluates the same flags server-side, and feeds the
 * result into posthog-js. Every useFeatureFlagEnabled caller updates as if
 * PostHog had answered. The values persist like normal flags, and a later
 * failed load leaves them in place (posthog-js skips empty responses), so the
 * next visit starts from them; a successful PostHog load replaces them.
 */

let remoteFailed = false;
const failureListeners = new Set();

export function trackFlagLoads(posthog) {
  posthog.onFeatureFlags((_flags, _variants, context) => {
    // No context = replay of already-loaded flags to a new listener, not a load.
    if (!context) return;
    remoteFailed = context.errorsLoading === true;
    if (remoteFailed) failureListeners.forEach((listener) => listener());
  });
}

export function remoteFlagsFailed() {
  return remoteFailed;
}

export function onRemoteFlagFailure(listener) {
  failureListeners.add(listener);
  return () => failureListeners.delete(listener);
}

/** `{ key: true|false|'variant' }` (PostHog /decide v3) → the v4 `flags` detail shape. */
export function toFlagDetails(flags) {
  return Object.fromEntries(Object.entries(flags).map(([key, value]) => {
    const variant = typeof value === 'string' ? value : undefined;
    return [key, { key, enabled: value === true || variant !== undefined, variant }];
  }));
}

let inFlight = null;

/** Fetch server-evaluated flags and load them into posthog-js. Resolves true when applied. */
export function applyServerFlags(posthog) {
  if (inFlight) return inFlight;
  inFlight = api.get(`${FASTIFY_BASE_URL}/featureFlags`)
    .then((res) => {
      const flags = res?.data?.success ? res.data.flags : null;
      if (!flags || !posthog?.featureFlags?.receivedFeatureFlags) return false;
      posthog.featureFlags.receivedFeatureFlags({ flags: toFlagDetails(flags) }, false);
      console.warn('PostHog feature flags could not load in this browser; using server-evaluated flags.');
      return true;
    })
    .catch(() => false)
    .finally(() => { inFlight = null; });
  return inFlight;
}
